import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import LoadingSpinner from '@ui/loading-spinner/loading-spinner';
import Modal from '@ui/modal/modal';
import CampaignCard from './campaign-card';
import CampaignEmptyState from './campaign-empty-state';
import CampaignsTable from './components/campaigns-table';
import CreateCampaignFlow from './create/create-campaign-flow';
import classes from './campaigns.module.css';
import useCampaignsLayout from './use-campaigns-layout';
import useLoadCampaigns from './use-load-campaigns';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import { selectLoadingScheduledEmails, selectScheduledEmails } from '@/store/slices/scheduled-emails/selectors';
import { scheduledEmailToCampaign } from '@/utils/scheduled-email-campaign';
import { deleteScheduledEmail } from '@/store/slices/scheduled-emails';

export default function Campaigns() {
  useLoadCampaigns();
  const scheduledEmails = useAppSelector(selectScheduledEmails);
  const loading = useAppSelector(selectLoadingScheduledEmails);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { layout, handleViewChange } = useCampaignsLayout();

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([]);
  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false);
  const [campaignUuidToDelete, setCampaignUuidToDelete] = useState<string | null>(null);

  const campaigns = useMemo(() => {
    return scheduledEmails?.map(scheduledEmailToCampaign) ?? [];
  }, [scheduledEmails]);

  const handleSelect = (id: string) => {
    setSelectedCampaignIds((prev) => (prev.includes(id) ? prev.filter((pId) => pId !== id) : [...prev, id]));
  };

  const handleSelectAll = (ids: string[]) => {
    setSelectedCampaignIds(ids);
  };

  const handleCreate = () => {
    setIsCreatingCampaign(true);
  };

  const handleEdit = (campaignUuid: string) => {
    navigate(`${campaignUuid}/edit`);
  };

  const handleDelete = (campaignUuid: string) => {
    setCampaignUuidToDelete(campaignUuid);
  };

  const handleConfirmDelete = () => {
    if (campaignUuidToDelete) {
      dispatch(deleteScheduledEmail(campaignUuidToDelete));
    }
    setCampaignUuidToDelete(null);
  };

  const deleteSelected = useCallback(() => {
    for (const id of selectedCampaignIds) {
      dispatch(deleteScheduledEmail(id));
    }
    setSelectedCampaignIds([]);
  }, [selectedCampaignIds, dispatch]);

  const getIconColor = (active: boolean) => {
    if (active) {
      return 'var(--global-ico-text-1)';
    }
    return 'var(--global-ico-text-3)';
  };

  if (loading) {
    return (
      <Container className={classes.loadingContainer}>
        <LoadingSpinner />
      </Container>
    );
  }

  if (campaigns.length === 0) {
    return (
      <>
        <Container className="flex-column gap-24" padding="0 var(--main-content-horizontal-padding)">
          <div className="padding-v-12">
            <Text tag="h3" className="H3" design="GlobalIcoText1">
              Campaigns
            </Text>
          </div>
          <CampaignEmptyState onCreate={handleCreate} />
        </Container>

        <CreateCampaignFlow isOpen={isCreatingCampaign} onClose={() => setIsCreatingCampaign(false)} />
      </>
    );
  }

  return (
    <Container className="flex-column gap-16" padding="0 var(--main-content-horizontal-padding)">
      <div className="flex ai-center jc-space-between">
        <Text tag="h4" className="H4" design="GlobalIcoText1">
          Campaigns
        </Text>

        <div className={classes.controls}>
          <Button
            clear
            borderRadius="100px"
            onClick={handleViewChange}
            backgroundColor="var(--local-switch-bg)"
            hoverOpacity={0.8}
            className="flex ai-center jc-space-evenly gap-4"
            designSize="small"
            padding="2px"
          >
            <Container
              background={layout === 'grid' ? 'var(--local-switch-active)' : undefined}
              className="square-28 radius-full flex-column ai-center jc-center"
            >
              <Icon name={ICONS.LAYOUT_GRID_4} size={12} color={getIconColor(layout === 'grid')} />
            </Container>
            <Container
              background={layout === 'list' ? 'var(--local-switch-active)' : undefined}
              className="square-28 radius-full flex-column ai-center jc-center"
            >
              <Icon name={ICONS.LAYOUT_FLIP_VERTICAL} size={12} color={getIconColor(layout === 'list')} />
            </Container>
          </Button>

          <Button onClick={handleCreate} backgroundColor={getThemeColorCSSVariable()} designSize="small">
            <Text
              color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
              className="L1 flex ai-center gap-6"
            >
              <Icon name={ICONS.RESEND_EMAIL} size={12} />
              Create Campaign
            </Text>
          </Button>
        </div>
      </div>

      <div className={layout === 'grid' ? classes.grid : undefined}>
        {layout === 'list' ? (
          <CampaignsTable
            campaigns={campaigns}
            onEdit={handleEdit}
            selectedIds={selectedCampaignIds}
            onSelect={handleSelect}
            onSelectAll={handleSelectAll}
            page={page}
            onPageChange={setPage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={setRowsPerPage}
            deleteSelected={deleteSelected}
          />
        ) : (
          campaigns.map((campaign) => (
            <CampaignCard
              key={campaign.uuid}
              campaign={campaign}
              onEdit={handleEdit}
              onDelete={handleDelete}
              view="grid"
            />
          ))
        )}
      </div>

      <CreateCampaignFlow isOpen={isCreatingCampaign} onClose={() => setIsCreatingCampaign(false)} />

      <Modal
        size="medium"
        open={!!campaignUuidToDelete}
        onRequestClose={() => setCampaignUuidToDelete(null)}
        RenderHeaderContent={() => (
          <Container height="70px" className="padding-t-24 padding-h-40">
            <Text tag="h4" className="H4">
              Delete Campaign
            </Text>
          </Container>
        )}
        RenderFooterContent={() => (
          <Container className="flex fd-row jc-flex-end ai-center padding-h-40 padding-v-24 gap-8">
            <Button onClick={() => setCampaignUuidToDelete(null)} design="LocalBttnSec" height="53px">
              Cancel
            </Button>
            <Button onClick={handleConfirmDelete} backgroundColor="var(--red-warning)" height="53px" hoverOpacity={0.8}>
              <Text className="L2" color="var(--white)">
                Delete
              </Text>
            </Button>
          </Container>
        )}
      >
        <Container className="padding-h-40 padding-v-16">
          <Text className="P1">Are you sure you want to delete this campaign? This action cannot be undone.</Text>
        </Container>
      </Modal>
    </Container>
  );
}
